import React, { forwardRef } from "react";
import { default as MUITooltip } from "@mui/material/Tooltip";
import styled from "styled-components";

const StyledMUITooltip = styled(MUITooltip)``;

/**
 * A customizable tooltip component built on top of Material-UI's Tooltip.
 * Provides additional functionality like custom offset positioning while maintaining
 * all the standard tooltip features.
 *
 * @component
 * @example
 * // Basic usage
 * <Tooltip title="This is a tooltip">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // With custom placement and arrow
 * <Tooltip
 *   title="Custom tooltip"
 *   placement="top"
 *   arrow
 *   offset={[0, 10]}
 * >
 *   <span>Hover for tooltip</span>
 * </Tooltip>
 *
 * @param {Object} props - The component props
 * @param {string} [props.id] - Optional ID for the tooltip element
 * @param {React.ReactNode} props.title - The content to display in the tooltip
 * @param {string} [props.placement="bottom"] - Position of the tooltip relative to the target element
 * @param {boolean} [props.arrow=false] - Whether to show an arrow pointing to the target element
 * @param {React.ReactNode} props.children - The element that triggers the tooltip on hover
 * @param {[number, number]} [props.offset] - Custom offset for tooltip positioning as [skidding, distance]
 * @param {React.Ref} ref - Forwarded ref to the underlying tooltip component
 *
 * @returns {React.ReactElement} The rendered tooltip component
 *
 * @see {@link https://mui.com/material-ui/react-tooltip/} Material-UI Tooltip documentation
 */

export const Tooltip = forwardRef(
  ({ id, title, placement, arrow, children, offset }, ref) => (
    <StyledMUITooltip
      id={id}
      title={title}
      placement={placement}
      arrow={arrow}
      ref={ref}
      slotProps={
        offset && {
          popper: {
            modifiers: [{ name: "offset", options: { offset } }],
          },
        }
      }
    >
      {children}
    </StyledMUITooltip>
  ),
);

Tooltip.displayName = "Tooltip";
